import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import cn from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'
import { EffectCoverflow, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { PaginationOptions, Swiper as SwiperType } from 'swiper/types'

import { Text } from '@ds'
import {
  GalaxyButton,
  SwiperArrows,
  useStars,
  useUserStar,
  webApp,
} from '@shared'
import { useIsUserProfileFetching, useUserActions } from '@store'

import stripes from '../../assets/profileStripes.json'
import { useUpdateUserStarSkin } from '../../hooks'
import styles from './StarSlider.module.scss'
import { StarSliderItem } from './components'

const hapticFeedback = webApp?.HapticFeedback

export const StarSlider = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const isProfileFetching = useIsUserProfileFetching()
  const { fetchProfile } = useUserActions()
  const { stars } = useStars()
  const { userStar } = useUserStar()
  const swiperRef = useRef<SwiperType | null>(null)

  const { handleUpdateUserStarSkin, loading: isUpdatingStar } =
    useUpdateUserStarSkin({
      onSuccess: () => {
        fetchProfile()
      },
    })

  const isEquipped = useMemo(() => {
    return userStar?.id === stars[activeIndex].id
  }, [userStar, stars, activeIndex])

  const pagination: PaginationOptions = {
    clickable: true,
    dynamicBullets: true,
    renderBullet: (_, className) => {
      return `<span class="${styles.paginationBullet} ${className}"></span>`
    },
  }

  const initialSlide = useMemo(() => {
    const index = stars.findIndex((item) => item.id === userStar?.id)
    const foundIndex = index === -1 ? 0 : index

    return foundIndex
  }, [userStar, stars])

  const currentStar = useMemo(() => {
    return stars[activeIndex]
  }, [stars, activeIndex])

  const isEquipButtonDisabled = useMemo(() => {
    return isEquipped || currentStar?.isAvailable === false
  }, [isEquipped, currentStar])

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    const currentIndex = swiper.realIndex
    setActiveIndex(currentIndex)
  }, [])

  const handleEquipStar = useCallback(() => {
    handleUpdateUserStarSkin(stars[activeIndex].id)
  }, [stars, activeIndex])

  const handlePrev = useCallback(() => {
    swiperRef.current?.slidePrev()
  }, [])

  const handleNext = useCallback(() => {
    swiperRef.current?.slideNext()
  }, [])

  const renderEquipButtonText = () => {
    if (isEquipped) {
      return (
        <Text type="body" center className={styles.equipButton__text}>
          Equipped
        </Text>
      )
    }

    return (
      <Text type="body" center className={styles.equipButton__text}>
        {isUpdatingStar || isProfileFetching ? 'Equipping...' : 'Equip'}
      </Text>
    )
  }

  return (
    <div className={styles.fixOverflow}>
      <div className={styles.container}>
        <div className={cn(styles.glowIcon, styles['glowIcon__left'])}>
          <DotLottieWorkerReact autoplay loop data={stripes} />
        </div>
        <div className={cn(styles.glowIcon, styles['glowIcon__right'])}>
          <DotLottieWorkerReact autoplay loop data={stripes} />
        </div>

        <div className={styles.title}>
          <Text align="center" className={styles.title__value}>
            Choose your
          </Text>
          <Text align="center" className={styles.title__value}>
            favorite star
          </Text>
        </div>
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          effect="coverflow"
          pagination={pagination}
          grabCursor
          centeredSlides
          initialSlide={initialSlide}
          loop
          slidesPerView="auto"
          onTouchStart={() => hapticFeedback?.impactOccurred('medium')}
          watchSlidesProgress
          onProgress={(swiper) => {
            swiper.slides.forEach((slideEl) => {
              const slide = slideEl as unknown as HTMLElement & {
                progress: number
              }
              const progress = slide.progress ?? 0
              const progressAbs = Math.abs(progress)
              const offsetY = Math.min(progressAbs * 80, 10)
              const opacity = Math.max(1 - progressAbs, 0.3)

              const inner = (slideEl as HTMLElement).querySelector<HTMLElement>(
                '[data-slide-inner]'
              )
              if (inner) {
                inner.style.transform = `translateY(${offsetY}rem)`
                inner.style.opacity = `${opacity}`
              }
            })
          }}
          onSlideChange={handleSlideChange}
          coverflowEffect={{
            rotate: 0,
            stretch: 40,
            depth: 150,
            modifier: 1,
            slideShadows: false,
            scale: 0.5,
          }}
          modules={[EffectCoverflow, Pagination]}
          className={styles.swiper}
        >
          {stars.map((item) => (
            <SwiperSlide className={styles.slide} key={item.id}>
              <div data-slide-inner className={styles.sliderContent}>
                <StarSliderItem
                  title={item.name}
                  src={item.appFiles || ''}
                  isAvailable={item.isAvailable}
                  isEquipped={item.id === userStar?.id}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className={styles.overlay} />
      </div>
      <SwiperArrows
        className={styles.swiperArrows}
        onClockPrev={handlePrev}
        onClickNext={handleNext}
      />
      <GalaxyButton
        withGlow={false}
        className={styles.equipButton}
        onClick={handleEquipStar}
        disabled={isEquipButtonDisabled}
      >
        {renderEquipButtonText()}
      </GalaxyButton>
    </div>
  )
}
